import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Group, Select, Skeleton, Stack, Text } from '@mantine/core';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import { PlotlyComponent } from 'visyn_core/plotly';

import { getScatterCategoryData, getTraceForClonotypeCategory } from './repertoire-analysis-utils';
import { LoadingContainer } from '../common/LoadingContainer';
import { UNKNOWN_LABEL } from '../constants/constants';
import { CELL_TYPE_OPTION, CLONOTYPE_SIZE_OPTION, IG_ISOTYPE_OPTION, LEIDEN_CLUSTER_OPTION } from '../constants/plot-options';
import type { CellResponse } from '../generated/generatedOpenAPI';
import { useEnsureSelectedClonotypeDefinition } from '../hooks/useEnsureSelectedClonotypeDefinition';
import { useGetClonotypeSizePerCell } from '../store/store';
import { getClonotypeEnumFromDefinition, getRangeForScatterPlot } from '../utils';

export enum ScatterScaleOption {
  Normal = '-',
  Log10 = 'log',
}

const scatterScaleOptions = [
  { label: 'Normal', value: ScatterScaleOption.Normal },
  { label: 'log10', value: ScatterScaleOption.Log10 },
];

interface UMIScatterPlotProps {
  hasIgIsotype: boolean;
  typeOfRanAnalysis: 'A' | 'B';
  cellData: CellResponse[] | undefined;
  isLoading?: boolean;
}

export function UMIScatterPlot({ hasIgIsotype, typeOfRanAnalysis, cellData, isLoading }: UMIScatterPlotProps) {
  const [userScatterCategoryOption, setUserScatterCategoryOption] = useState<string | null>(null);
  const [scatterScaleOption, setScatterScaleOption] = useState<ScatterScaleOption>(ScatterScaleOption.Log10);

  const categorySelectorData = React.useMemo(() => {
    const data = [CLONOTYPE_SIZE_OPTION];
    if (typeOfRanAnalysis === 'A') {
      data.push(CELL_TYPE_OPTION, LEIDEN_CLUSTER_OPTION);
    }
    if (hasIgIsotype) {
      data.push(IG_ISOTYPE_OPTION);
    }
    return data;
  }, [hasIgIsotype, typeOfRanAnalysis]);

  // Until the user explicitly picks a category, follow the data: this component mounts before
  // the repertoire query resolves, so the default has to react to hasIgIsotype arriving late.
  const scatterCategoryOption =
    userScatterCategoryOption ?? (categorySelectorData.includes(IG_ISOTYPE_OPTION) ? IG_ISOTYPE_OPTION.value : CLONOTYPE_SIZE_OPTION.value);

  const { id: experimentId } = useParams();

  const selectedClonotypeDefinition = useEnsureSelectedClonotypeDefinition();

  const { data: cellClonotypeData, isLoading: isCellClonotypeDataLoading } = useGetClonotypeSizePerCell(
    {
      id: experimentId!,
      clonotypeDefinition: getClonotypeEnumFromDefinition(selectedClonotypeDefinition),
    },
    { skip: !experimentId || scatterCategoryOption !== CLONOTYPE_SIZE_OPTION.value },
  );

  const scatterPlotData: Plotly.Data[] = React.useMemo(() => {
    const scatterValues = cellData;

    if (!scatterValues) {
      return [];
    }

    if (scatterCategoryOption === IG_ISOTYPE_OPTION.value) {
      return getScatterCategoryData(scatterValues, 'irVdj1CCall');
    }
    if (scatterCategoryOption === CELL_TYPE_OPTION.value) {
      return getScatterCategoryData(scatterValues, 'majorityVoting');
    }
    if (scatterCategoryOption === LEIDEN_CLUSTER_OPTION.value) {
      return getScatterCategoryData(scatterValues, 'leiden');
    }
    // if (scatterCategoryOption === 'topClonotypes') {
    //   const groupedByClonotypes = groupBy(scatterValues, 'clonotypeId');
    //   const groupedByClonotypesItems = Object.entries(groupedByClonotypes).map(([key, value]) => [key, value]) as Array<[string, CellResponse[]]>;

    //   groupedByClonotypesItems.sort((a, b) => b[1].length - a[1].length);

    //   const top15Clonotypes = groupedByClonotypesItems.slice(0, 15);
    //   const restClonotypes = groupedByClonotypesItems.slice(15);
    //   const scatterClonotypesData = top15Clonotypes.map(([key, value]) => {
    //     if (key === 'null') {
    //       return null;
    //     }
    //     const dataX = value.map((item) => item.irVdj1DuplicateCount);
    //     const dataY = value.map((item) => item.irVj1DuplicateCount);
    //     const barCodeHoverData = value.map((item) => item.barcode);
    //     return {
    //       x: dataX,
    //       y: dataY,
    //       name: key,
    //       type: 'scatter',
    //       mode: 'markers',
    //       hovertext: barCodeHoverData,
    //       hoverinfo: 'text',
    //     };
    //   });

    //   const restClonotypesValues = restClonotypes.flatMap(([, value]) => value);

    //   const dataX = restClonotypesValues.map((item) => item.irVdj1DuplicateCount);
    //   const dataY = restClonotypesValues.map((item) => item.irVj1DuplicateCount);
    //   const barCodeHoverData = restClonotypesValues.map((item) => item.barcode);

    //   scatterClonotypesData.push({
    //     x: dataX,
    //     y: dataY,
    //     name: 'Rest',
    //     type: 'scatter',
    //     mode: 'markers',
    //     hovertext: barCodeHoverData,
    //     hoverinfo: 'text',
    //     // @ts-expect-error untyped
    //     marker: {
    //       color: 'grey',
    //     },
    //   });

    //   return scatterClonotypesData.filter(Boolean);
    // }
    if (scatterCategoryOption === CLONOTYPE_SIZE_OPTION.value) {
      const keyedCellClonotypeData = keyBy(cellClonotypeData, 'cellId');
      const groupedByClonotypeSize = groupBy(scatterValues, (cell) => {
        const clonotypeSize = keyedCellClonotypeData[cell.id]?.clonotypeSize || 0;
        if (clonotypeSize === 1) {
          return '1';
        }
        if (clonotypeSize >= 2 && clonotypeSize <= 5) {
          return '2-5';
        }
        if (clonotypeSize >= 6 && clonotypeSize <= 9) {
          return '6-9';
        }
        if (clonotypeSize >= 10 && clonotypeSize <= 19) {
          return '10-19';
        }
        if (clonotypeSize >= 20) {
          return '20+';
        }
        return UNKNOWN_LABEL;
      });

      const traces = Object.entries(groupedByClonotypeSize).map(([size, groupData], idx) => getTraceForClonotypeCategory(size, groupData, idx));
      const sortBy = ['1', '2-5', '6-9', '10-19', '20+', UNKNOWN_LABEL];
      const sortedTraces = traces.sort((a, b) => {
        const aIndex = sortBy.indexOf(a.name);
        const bIndex = sortBy.indexOf(b.name);
        return aIndex - bIndex;
      });
      return sortedTraces;
    }

    return [];
  }, [cellClonotypeData, cellData, scatterCategoryOption]);

  const { scatterRangeStartX, scatterRangeEndX, scatterRangeStartY, scatterRangeEndY } = React.useMemo(() => {
    if (!cellData) {
      return {
        scatterRangeStartX: 0,
        scatterRangeEndX: 0,
        scatterRangeStartY: 0,
        scatterRangeEndY: 0,
      };
    }
    const [rangeStartX, rangeEndX] = getRangeForScatterPlot(cellData, 'irVdj1DuplicateCount', scatterScaleOption);
    const [rangeStartY, rangeEndY] = getRangeForScatterPlot(cellData, 'irVj1DuplicateCount', scatterScaleOption);
    return {
      scatterRangeStartX: rangeStartX,
      scatterRangeEndX: rangeEndX,
      scatterRangeStartY: rangeStartY,
      scatterRangeEndY: rangeEndY,
    };
  }, [cellData, scatterScaleOption]);

  if (isLoading || isCellClonotypeDataLoading) {
    return <LoadingContainer height={300} />;
  }

  if (!cellData) {
    return <Skeleton height={300} />;
  }

  return (
    <Stack align="center" w="100%">
      <Group mb="sm">
        <Text size="sm">Category:</Text>
        <Select
          data-testid="scatter-category-selector"
          value={scatterCategoryOption}
          onChange={(value) => {
            if (value) {
              setUserScatterCategoryOption(value);
            }
          }}
          data={categorySelectorData}
          size="sm"
        />

        <Text size="sm" ml="md">
          Scale:
        </Text>
        <Select
          data-testid="scatter-scale-selector"
          value={scatterScaleOption}
          onChange={(value) => {
            if (value) {
              setScatterScaleOption(value as ScatterScaleOption);
            }
          }}
          data={scatterScaleOptions}
          size="sm"
        />
      </Group>
      <div data-testid="umi-scatter-plot">
        <PlotlyComponent
          data={scatterPlotData}
          useResizeHandler
          style={{ width: '100%', maxWidth: 900 }}
          layout={{
            showlegend: true,
            xaxis: {
              type: scatterScaleOption,
              title: {
                text: 'UMI Count (VH)',
                font: { size: 16 },
              },
              range: [scatterRangeStartX, scatterRangeEndX],
            },
            yaxis: {
              type: scatterScaleOption,
              title: {
                text: 'UMI Count (VL)',
                font: { size: 16 },
              },
              range: [scatterRangeStartY, scatterRangeEndY],
            },
            legend: {
              x: 1.14,
              xanchor: 'right',
              y: scatterCategoryOption === CELL_TYPE_OPTION.value && scatterScaleOption === ScatterScaleOption.Log10 ? 0.05 : undefined,
            },
            height: 600,
            margin: { t: 50 },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
      </div>
    </Stack>
  );
}
